import { BuildingStorefrontIcon, CubeIcon, ShoppingBagIcon, TagIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import { IPart, PartStatus } from "../../models/Part";
import { Marketplace, MarketplaceListingStatus } from "../../models/Publishing";
import classNames from "../../utils/classNames";
import Tooltip from "../shared/Tooltip";
import ViewOnButton from "../shared/ViewOnButton";

interface StatusIconsProps {
  part: IPart;
  // Whether this customer sells through eBay/Shopify. When they don't, the marketplace pills are
  // permanently "Not published"/"Not on Shopify" on every row - four columns of noise reporting on
  // sales channels that aren't part of the business. Decided by the caller, which already knows
  // the customer, rather than looked up per row.
  showMarketplaces?: boolean;
}

export default function StatusIcons({ part, showMarketplaces = true }: StatusIconsProps) {
  const { t } = useTranslation("stock");
  const offer = part.offers?.[0];
  // Per-marketplace rows know about a refused or in-flight publish, which the offer's dates don't.
  const rowFor = (marketplace: Marketplace) =>
    part.marketplaceListings?.find((x) => x.marketplace === marketplace);
  const ebayRow = rowFor(Marketplace.Ebay);
  const shopifyRow = rowFor(Marketplace.Shopify);
  const partProRow = rowFor(Marketplace.PartPro);

  const ebay = (() => {
    if (part.soldDate) return { label: t("Sold"), color: "bg-emerald-100 text-emerald-600" };
    if (ebayRow?.status === MarketplaceListingStatus.Failed) return { label: `${t("Publish failed")}${ebayRow.lastError ? `: ${ebayRow.lastError}` : ""}`, color: "bg-rose-100 text-rose-600" };
    if (ebayRow?.status === MarketplaceListingStatus.Publishing) return { label: t("Listing in progress"), color: "bg-purple-100 text-purple-600" };
    if (part.listingProcessing) return { label: t("Listing in progress"), color: "bg-purple-100 text-purple-600" };
    if (offer?.publishedOn && offer?.listingStarted && new Date(offer.listingStarted) < new Date()) return { label: t("Active on eBay"), color: "bg-blue-100 text-blue-600" };
    if (offer?.listingEnded && new Date(offer.listingEnded) < new Date()) return { label: t("Ended - not sold"), color: "bg-rose-100 text-rose-600" };
    if (offer?.publishedOn) return { label: t("Published"), color: "bg-amber-100 text-amber-600" };
    return { label: t("Not published"), color: "bg-slate-100 text-slate-400" };
  })();
  const order = (() => {
    if (part.shippedOn) return { label: t("Shipped"), color: "bg-emerald-100 text-emerald-600" };
    if (part.pickedOn) return { label: t("Picked"), color: "bg-blue-100 text-blue-600" };
    if (part.soldDate) return { label: t("Awaiting pick"), color: "bg-amber-100 text-amber-600" };
    return { label: t("No order"), color: "bg-slate-100 text-slate-400" };
  })();
  const shopify = (() => {
    if (shopifyRow?.status === MarketplaceListingStatus.Failed) return { label: `${t("Shopify publish failed")}${shopifyRow.lastError ? `: ${shopifyRow.lastError}` : ""}`, color: "bg-rose-100 text-rose-600" };
    if (shopifyRow?.status === MarketplaceListingStatus.Publishing) return { label: t("Publishing to Shopify"), color: "bg-purple-100 text-purple-600" };
    if (part.isPublishedOnShopify || shopifyRow?.status === MarketplaceListingStatus.Live) return { label: t("On Shopify"), color: "bg-emerald-100 text-emerald-600" };
    return { label: t("Not on Shopify"), color: "bg-slate-100 text-slate-400" };
  })();
  // Only drawn when the part has ever been sent to PartPro - most customers don't use it.
  const partPro = (() => {
    if (!partProRow) return null;
    if (partProRow.status === MarketplaceListingStatus.Failed) return { label: `${t("PartPro publish failed")}${partProRow.lastError ? `: ${partProRow.lastError}` : ""}`, color: "bg-rose-100 text-rose-600" };
    if (partProRow.status === MarketplaceListingStatus.Publishing) return { label: t("Publishing to PartPro"), color: "bg-purple-100 text-purple-600" };
    if (partProRow.status === MarketplaceListingStatus.Live || partProRow.status === MarketplaceListingStatus.Sold) return { label: t("On PartPro"), color: "bg-emerald-100 text-emerald-600" };
    return { label: t("Not on PartPro"), color: "bg-slate-100 text-slate-400" };
  })();
  const partStatusValue = part.partStatus as PartStatus | null | undefined;
  const partInfo = (() => {
    if (partStatusValue === null || partStatusValue === undefined) return { label: t("Status N/A"), color: "bg-slate-100 text-slate-400" };
    const label = PartStatus[partStatusValue] ? t(PartStatus[partStatusValue]) : t("Unknown");
    if (partStatusValue === PartStatus.Dismantled) return { label, color: "bg-emerald-100 text-emerald-600" };
    if (partStatusValue === PartStatus["Pending Dismantle"]) return { label, color: "bg-amber-100 text-amber-600" };
    if (partStatusValue === PartStatus.QC) return { label, color: "bg-indigo-100 text-indigo-600" };
    if ([PartStatus.Damaged, PartStatus.Missing, PartStatus.Returned, PartStatus.Deleted].includes(partStatusValue)) return { label, color: "bg-rose-100 text-rose-600" };
    if (partStatusValue === PartStatus.Sold) return { label, color: "bg-blue-100 text-blue-600" };
    return { label, color: "bg-slate-100 text-slate-500" };
  })();

  const Pill = ({ color, label, Icon }: { color: string; label: string; Icon: typeof TagIcon }) => (
    <Tooltip content={label}>
      <span className={classNames("inline-flex h-7 w-7 items-center justify-center rounded-full", color)}>
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
    </Tooltip>
  );

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {showMarketplaces && (
        <>
          <Pill color={ebay.color} label={`eBay: ${ebay.label}`} Icon={TagIcon} />
          <Pill color={shopify.color} label={`Shopify: ${shopify.label}`} Icon={BuildingStorefrontIcon} />
          {partPro && <Pill color={partPro.color} label={`PartPro: ${partPro.label}`} Icon={WrenchScrewdriverIcon} />}
        </>
      )}
      <Pill color={order.color} label={`${t("Order")}: ${order.label}`} Icon={ShoppingBagIcon} />
      <Pill color={partInfo.color} label={`${t("Part")}: ${partInfo.label}`} Icon={CubeIcon} />
      {/* Also gated: this only ever links out to an eBay or Shopify listing, so it has nowhere to
          go for a customer who doesn't sell there - historic data aside. */}
      {showMarketplaces && (part.ebayItemNumber || part.isPublishedOnShopify) && (
        <ViewOnButton part={part} compact />
      )}
    </div>
  );
}
